"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import {
  MapPinIcon,
  ChevronDownIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  LocationData,
  LAGOS_STATE,
  OYO_STATE,
  LAGOS_LGAS,
  OYO_LGAS,
  LAGOS_NEIGHBORHOODS,
  OYO_NEIGHBORHOODS,
  parseLocationString,
  formatLocationString,
  fetchActiveLocations,
  DatabaseCountry,
  DatabaseState,
} from "@/lib/locationUtils";
import {
  searchGoogleMapsLocations,
  GoogleMapsLocation,
  validateLocationForStorage,
} from "@/lib/googleMapsUtils";
import { GOOGLE_MAPS_CONFIG } from "@/config/maps";
import { useSystemSettings } from "@/contexts/SystemSettingsContext";
import { useToast } from "@/contexts/ToastContext";
import {
  locationIdsFromHierarchy,
  resolveLocationIdsFromLocationString,
  type HierarchicalLocationOnChange,
} from "@/lib/locationIds";
import React from "react";

interface HierarchicalLocationSelectProps {
  value: string;
  onChange: HierarchicalLocationOnChange;
  placeholder?: string;
  className?: string;
}

export default function HierarchicalLocationSelect({
  value,
  onChange,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  placeholder = "Select your location",
  className = "",
}: HierarchicalLocationSelectProps) {
  // Get toast function
  const { showToast } = useToast();

  // State for current selections
  const [selectedState, setSelectedState] = useState<LocationData | null>(null);
  const [selectedLGA, setSelectedLGA] = useState<LocationData | null>(null);
  const [selectedNeighborhood, setSelectedNeighborhood] =
    useState<LocationData | null>(null);

  // State for search queries
  const [stateQuery, setStateQuery] = useState("");
  const [locationQuery, setLocationQuery] = useState("");

  // State for dropdown visibility
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);

  // State for Google Maps results
  const [googleMapsResults, setGoogleMapsResults] = useState<
    GoogleMapsLocation[]
  >([]);
  const [isSearchingGoogleMaps, setIsSearchingGoogleMaps] = useState(false);
  const [selectedGoogleMapsLocation, setSelectedGoogleMapsLocation] =
    useState<GoogleMapsLocation | null>(null);

  // State for database-driven locations
  const [activeCountries, setActiveCountries] = useState<DatabaseCountry[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);

  // Use simple system settings context
  const { googleMapsEnabled, isLoading: systemSettingsLoading } =
    useSystemSettings();

  // Refs for handling outside clicks
  const stateRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef<HTMLDivElement>(null);

  // Don't use Google Maps if disabled or settings are loading
  const shouldUseGoogleMaps = !systemSettingsLoading && googleMapsEnabled;

  // Clear Google Maps results when disabled
  useEffect(() => {
    if (!shouldUseGoogleMaps) {
      const timer = window.setTimeout(() => {
        setGoogleMapsResults([]);
        setSelectedGoogleMapsLocation(null);
      }, 0);
      return () => window.clearTimeout(timer);
    }
  }, [shouldUseGoogleMaps]);

  // Fetch active locations from database
  useEffect(() => {
    const loadActiveLocations = async () => {
      setIsLoadingLocations(true);
      try {
        const countries = await fetchActiveLocations();
        setActiveCountries(countries);
      } catch (error) {
        console.error("Error loading active locations:", error);
        // Fallback to hardcoded states if database fails
        setActiveCountries([]);
      } finally {
        setIsLoadingLocations(false);
      }
    };

    loadActiveLocations();
  }, []);

  // Get available states from active countries (prioritize Nigeria)
  const getAvailableStates = React.useCallback((): LocationData[] => {
    if (isLoadingLocations) return [];

    // Convert database states to LocationData format
    const states: LocationData[] = [];

    activeCountries.forEach((country) => {
      country.states.forEach((state: DatabaseState) => {
        states.push({
          id: state.id,
          name: state.name,
          type: "state",
          coordinates: state.center
            ? { lat: state.center.lat, lng: state.center.lng }
            : undefined,
        });
      });
    });

    // If no database states available, fallback to hardcoded states
    if (states.length === 0) {
      return [LAGOS_STATE, OYO_STATE];
    }

    return states;
  }, [activeCountries, isLoadingLocations]);

  // Memoize states array to prevent unnecessary re-renders
  const STATES = React.useMemo(
    () => getAvailableStates(),
    [getAvailableStates],
  );

  // Helper to get LGAs based on selected state
  const getLGAsForState = (state: LocationData | null) => {
    if (!state) return [];
    // Compare by name for broader compatibility if IDs differ (DB vs hardcoded)
    if (state.name.toLowerCase() === LAGOS_STATE.name.toLowerCase())
      return LAGOS_LGAS;
    if (state.name.toLowerCase() === OYO_STATE.name.toLowerCase())
      return OYO_LGAS;
    // Add a log if a state is selected but not matched for local data
    // console.warn(`[HLS WARNING] No hardcoded LGAs found for selected state: ${state.name} (ID: ${state.id})`);
    return [];
  };

  // Helper to get neighborhoods based on selected state
  const getNeighborhoodsForState = (state: LocationData | null) => {
    if (!state) return [];
    // Compare by name for broader compatibility
    if (state.name.toLowerCase() === LAGOS_STATE.name.toLowerCase())
      return LAGOS_NEIGHBORHOODS;
    if (state.name.toLowerCase() === OYO_STATE.name.toLowerCase())
      return OYO_NEIGHBORHOODS;
    // Add a log if a state is selected but not matched for local data
    // console.warn(`[HLS WARNING] No hardcoded Neighborhoods found for selected state: ${state.name} (ID: ${state.id})`);
    return [];
  };

  // Search Google Maps for locations
  const searchGoogleMapsForLocations = useCallback(
    async (query: string) => {
      if (!shouldUseGoogleMaps || !query || query.length < 2) {
        setGoogleMapsResults([]);
        return;
      }

      setIsSearchingGoogleMaps(true);
      try {
        // Get the first active country for the search context
        let searchQuery = query;
        if (activeCountries.length > 0) {
          // Prioritize Nigeria if available, otherwise use the first active country
          const nigeria = activeCountries.find(
            (country) =>
              country.code === "NG" ||
              country.name.toLowerCase().includes("nigeria"),
          );
          const targetCountry = nigeria || activeCountries[0];
          searchQuery = `${query}, ${targetCountry.name}`;
        }

        const result = await searchGoogleMapsLocations(
          searchQuery,
          GOOGLE_MAPS_CONFIG.DEFAULT_CENTER,
          GOOGLE_MAPS_CONFIG.DEFAULT_RADIUS,
          selectedState?.name,
        );

        setGoogleMapsResults(result.locations);
      } catch (error) {
        console.error("Error searching Google Maps:", error);
        setGoogleMapsResults([]);
      } finally {
        setIsSearchingGoogleMaps(false);
      }
    },
    [shouldUseGoogleMaps, activeCountries, selectedState],
  );

  // Set initial values from props.value if it exists
  useEffect(() => {
    // Don't try to parse the value until location data is loaded
    if (isLoadingLocations) {
      return;
    }

    if (value) {
      const { state, lga, neighborhood } = parseLocationString(value);

      // Check if this looks like a Google Maps formatted address (contains commas and doesn't match local format)
      if (!state && value.includes(",")) {
        // This is likely a Google Maps formatted address
        const timer = window.setTimeout(() => {
          setLocationQuery(value.split(",")[0]); // Use the first part as the display name
          setSelectedGoogleMapsLocation({
            placeId: "existing",
            name: value.split(",")[0],
            formattedAddress: value,
            coordinates: { lat: 0, lng: 0 }, // Placeholder coordinates for existing selections
            types: ["establishment"], // Placeholder types for existing selections
          });
        }, 0);
        return () => window.clearTimeout(timer);
      }

      // Handle local location format
      const stateData = STATES.find((s) => s.name === state);

      if (stateData) {
        const timer = window.setTimeout(() => {
          setSelectedState(stateData);
          setStateQuery(stateData.name);

          if (lga) {
            // Find LGA based on the selected state
            const stateLGAs = getLGAsForState(stateData);
            const lgaData = stateLGAs.find((l) => l.name === lga);

            if (lgaData) {
              setSelectedLGA(lgaData);
            }
          }

          if (neighborhood && lga) {
            // Find neighborhood based on the selected state and LGA
            const stateNeighborhoods = getNeighborhoodsForState(stateData);
            const lgaData = getLGAsForState(stateData).find(
              (l) => l.name === lga,
            );

            if (lgaData && stateNeighborhoods.length > 0) {
              const neighborhoodData = stateNeighborhoods.find(
                (n) => n.name === neighborhood && n.parent === lgaData.id,
              );

              if (neighborhoodData) {
                setSelectedNeighborhood(neighborhoodData);
                setLocationQuery(neighborhoodData.name);
              }
            }
          }
        }, 0);
        return () => window.clearTimeout(timer);
      }
    } else {
      // Clear all selections when value is empty
      const timer = window.setTimeout(() => {
        setSelectedState(null);
        setSelectedLGA(null);
        setSelectedNeighborhood(null);
        setSelectedGoogleMapsLocation(null);
        setLocationQuery("");
        setStateQuery("");
      }, 0);
      return () => window.clearTimeout(timer);
    }
  }, [value, isLoadingLocations, STATES]); // Add dependencies to ensure it re-runs when data is available

  // Handle Google Maps search with debounce
  useEffect(() => {
    const searchTimeout = setTimeout(() => {
      if (shouldUseGoogleMaps) {
        searchGoogleMapsForLocations(locationQuery);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [locationQuery, shouldUseGoogleMaps, searchGoogleMapsForLocations]);

  // Handle click outside for dropdowns
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        stateRef.current &&
        !stateRef.current.contains(event.target as Node)
      ) {
        setStateDropdownOpen(false);
      }
      if (
        locationRef.current &&
        !locationRef.current.contains(event.target as Node)
      ) {
        setLocationDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Group neighborhoods or LGAs by LGA for the dropdown display (local data)
  const groupedLocations: {
    lga: LocationData;
    neighborhoods: LocationData[];
  }[] = [];

  if (selectedState) {
    const stateLGAs = getLGAsForState(selectedState);
    const stateNeighborhoods = getNeighborhoodsForState(selectedState);

    if (stateNeighborhoods.length > 0) {
      stateLGAs.forEach((lga) => {
        const potentialNeighborhoods = stateNeighborhoods.filter(
          (n) => n.parent === lga.id,
        );
        let neighborhoodsToShow = potentialNeighborhoods;

        if (locationQuery && locationQuery.length >= 1) {
          const queryLower = locationQuery.toLowerCase();
          neighborhoodsToShow = potentialNeighborhoods.filter((n) =>
            n.name.toLowerCase().includes(queryLower),
          );
        }

        if (neighborhoodsToShow.length > 0) {
          groupedLocations.push({
            lga,
            neighborhoods: neighborhoodsToShow,
          });
        }
      });
    } else {
      let lgasToShow = stateLGAs;

      if (locationQuery && locationQuery.length >= 1) {
        const queryLower = locationQuery.toLowerCase();
        lgasToShow = stateLGAs.filter((lga) =>
          lga.name.toLowerCase().includes(queryLower),
        );
      }

      if (lgasToShow.length > 0) {
        groupedLocations.push({
          lga: {
            id: selectedState.id,
            name: selectedState.name,
            type: "state",
          },
          neighborhoods: lgasToShow,
        });
      }
    }
  }

  // Handle state selection
  const handleStateSelect = (state: LocationData) => {
    // Reset selections when state changes
    setSelectedLGA(null);
    setSelectedNeighborhood(null);
    setSelectedGoogleMapsLocation(null);
    setLocationQuery("");
    setGoogleMapsResults([]);

    setSelectedState(state);
    setStateQuery(state.name);
    setStateDropdownOpen(false);

    // If only state is selected, update the value
    onChange(
      formatLocationString(undefined, undefined, state.name),
      undefined,
      undefined,
      locationIdsFromHierarchy(state),
    );
  };

  // Handle Google Maps location selection
  const handleGoogleMapsLocationSelect = (location: GoogleMapsLocation) => {
    // SECURITY: Validate location before selection
    const validationResult = validateLocationForStorage(location);

    if (!validationResult.isValid) {
      showToast(
        validationResult.message || "Invalid location selected",
        "error",
      );
      return;
    }

    // Use the area name instead of the street name if needed
    const displayName = validationResult.areaName || location.name;
    const formattedAddress = location.formattedAddress.replace(
      location.name,
      displayName,
    );

    onChange(
      formattedAddress,
      location.coordinates,
      location.placeId,
      resolveLocationIdsFromLocationString(formattedAddress),
    );
    setLocationQuery(displayName);
    setLocationDropdownOpen(false);
    setGoogleMapsResults([]);

    // Clear local selections and set Google Maps selection
    setSelectedState(null);
    setSelectedLGA(null);
    setSelectedNeighborhood(null);
    setSelectedGoogleMapsLocation({
      ...location,
      name: displayName,
      formattedAddress,
    });

    // Show privacy message
    if (validationResult.message) {
      showToast(validationResult.message, "info");
    }
  };

  // Handle LGA selection (when no neighborhoods are available)
  const handleLGASelect = (lga: LocationData) => {
    setSelectedLGA(lga);
    setSelectedGoogleMapsLocation(null); // Clear Google Maps selection
    setLocationQuery(lga.name);
    setLocationDropdownOpen(false);

    // Update the value with state and LGA coordinates
    if (selectedState) {
      onChange(
        formatLocationString(undefined, lga.name, selectedState.name),
        lga.coordinates,
        undefined,
        locationIdsFromHierarchy(selectedState, lga),
      );
    }
  };

  // Handle neighborhood selection
  const handleNeighborhoodSelect = (
    neighborhood: LocationData,
    lga: LocationData,
  ) => {
    setSelectedNeighborhood(neighborhood);
    setSelectedLGA(lga);
    setSelectedGoogleMapsLocation(null); // Clear Google Maps selection
    setLocationQuery(neighborhood.name);
    setLocationDropdownOpen(false);

    // Update the value with complete location hierarchy and coordinates
    if (selectedState) {
      onChange(
        formatLocationString(neighborhood.name, lga.name, selectedState.name),
        neighborhood.coordinates,
        undefined,
        locationIdsFromHierarchy(selectedState, lga, neighborhood),
      );
    }
  };

  // Clear location selection
  const handleClearLocation = () => {
    setLocationQuery("");
    setSelectedNeighborhood(null);
    setSelectedLGA(null);
    setSelectedGoogleMapsLocation(null);
    setGoogleMapsResults([]);
    if (selectedState) {
      onChange(
        formatLocationString(undefined, undefined, selectedState.name),
        undefined,
        undefined,
        locationIdsFromHierarchy(selectedState),
      );
    }
  };

  return (
    <div className={className}>
      {/* Search help message */}
      <div className="mb-2 text-xs text-text-light">
        {GOOGLE_MAPS_CONFIG.SECURITY_CONFIG.MESSAGES.SEARCH_HELP}
      </div>
      <div className="mb-2 text-xs text-blue-600">
        Select the area where you&apos;re located for better matching with
        nearby items
      </div>
      <div className="flex flex-row gap-2">
        {/* State Selection */}
        <div ref={stateRef} className="relative flex-1">
          <div className="relative">
            <MapPinIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-text-light" />
            <input
              type="text"
              value={stateQuery}
              onChange={(e) => setStateQuery(e.target.value)}
              onFocus={() => setStateDropdownOpen(true)}
              placeholder="Select your state"
              className="w-full rounded-lg border border-border py-2 pl-10 pr-8 placeholder:text-sm "
              readOnly
            />
            <ChevronDownIcon className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-text-light" />
          </div>

          {stateDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full rounded-md bg-surface py-1 shadow-lg ring-1 ring-black ring-opacity-5 max-h-60 overflow-auto">
              {STATES.map((state) => (
                <div
                  key={state.id}
                  className="px-4 py-2 cursor-pointer hover:bg-tertiary"
                  onClick={() => handleStateSelect(state)}
                >
                  {state.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Searchable Location Selection */}
        {selectedState && (
          <div ref={locationRef} className="relative flex-1">
            <div className="relative">
              <MapPinIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-text-light" />
              <input
                type="text"
                value={locationQuery}
                onChange={(e) => {
                  setLocationQuery(e.target.value);
                  setLocationDropdownOpen(true);
                }}
                onFocus={() => setLocationDropdownOpen(true)}
                placeholder={
                  shouldUseGoogleMaps
                    ? "Search for your street or area..."
                    : "Browse or search areas..."
                }
                className="w-full rounded-lg border border-border py-2 pl-10 pr-8 placeholder:text-sm"
              />
              {locationQuery && (
                <button
                  onClick={handleClearLocation}
                  className="absolute right-8 top-1/2 -translate-y-1/2 text-text-light hover:text-text-light"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
              <ChevronDownIcon className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-text-light" />
            </div>

            {locationDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full rounded-md bg-surface py-1 shadow-lg ring-1 ring-black ring-opacity-5 max-h-60 overflow-auto">
                {/* Google Maps Results */}
                {shouldUseGoogleMaps && (
                  <>
                    {isSearchingGoogleMaps ? (
                      <div className="px-4 py-2 text-sm text-text-light">
                        Searching...
                      </div>
                    ) : googleMapsResults.length > 0 ? (
                      <>
                        <div className="px-2 py-1 text-xs font-semibold text-blue-500 border-b">
                          GMAPS SEARCH RESULTS
                        </div>
                        {googleMapsResults.map((location) => {
                          const validation =
                            validateLocationForStorage(location);
                          const displayName =
                            validation.areaName || location.name;
                          const isStreetLevel = location.types.some((type) =>
                            ["route", "street_address"].includes(type),
                          );

                          return (
                            <div
                              key={location.placeId}
                              className="flex cursor-pointer items-center px-4 py-2 hover:bg-tertiary"
                              onClick={() =>
                                handleGoogleMapsLocationSelect(location)
                              }
                            >
                              <MapPinIcon
                                className={`mr-2 h-5 w-5 ${isStreetLevel ? "text-orange-400" : "text-blue-400"}`}
                              />
                              <div>
                                <div className="font-medium">
                                  {location.name}
                                </div>
                                <div className="text-xs text-text-light">
                                  {isStreetLevel ? (
                                    <>
                                      <span className="text-orange-600">
                                        Street address
                                      </span>
                                      <span className="mx-1">•</span>
                                      Will be saved as: {displayName}
                                    </>
                                  ) : (
                                    location.formattedAddress
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {groupedLocations.length > 0 && (
                          <div className="border-t border-border"></div>
                        )}
                      </>
                    ) : (
                      locationQuery.length >= 2 && (
                        <div className="px-4 py-2 text-sm text-text-light">
                          No locations found. Try a different search term.
                        </div>
                      )
                    )}
                  </>
                )}

                {/* Local Data Results */}
                {groupedLocations.map((group) => (
                  <div key={group.lga.id} className="px-2 py-1">
                    <div className="px-2 py-1 text-xs font-semibold text-text-light">
                      {group.lga.name.toUpperCase()}
                    </div>
                    {group.neighborhoods.map((location) => (
                      <div
                        key={location.id}
                        className="flex cursor-pointer items-center px-2 py-2 hover:bg-tertiary"
                        onClick={() => {
                          if (location.type === "lga") {
                            handleLGASelect(location);
                          } else {
                            handleNeighborhoodSelect(location, group.lga);
                          }
                        }}
                      >
                        <MapPinIcon className="mr-2 h-5 w-5 text-text-light" />
                        <div>
                          <div className="font-medium">{location.name}</div>
                          <div className="text-xs text-text-light">
                            {location.type === "lga"
                              ? `${selectedState?.name}, Nigeria`
                              : `${group.lga.name}, ${selectedState?.name}, Nigeria`}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}

                {/* Conditional Message for No Local Locations */}
                {groupedLocations.length === 0 && (
                  <div className="px-4 py-2 text-sm text-text-light">
                    {shouldUseGoogleMaps
                      ? // When Google Maps is enabled but returns no results and no local data
                        locationQuery && locationQuery.length >= 1
                        ? `No locations found for "${locationQuery}". Try a different search term or browse areas below.`
                        : selectedState
                          ? `Search for areas in ${selectedState.name} or browse available options below.`
                          : `Please select a state first.`
                      : // When Google Maps is disabled and no local data
                        locationQuery && locationQuery.length >= 1
                        ? `No local locations found for "${locationQuery}"`
                        : selectedState
                          ? `No local LGAs/areas available for ${selectedState.name}`
                          : `Please select a state to see local options.`}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selected location display with privacy notice */}
      {(selectedState ||
        selectedLGA ||
        selectedNeighborhood ||
        selectedGoogleMapsLocation) && (
        <div className="mt-2 text-xs text-text-light">
          <div className="flex items-start">
            <div className="font-medium mr-2">Selected location:</div>
            <div className="flex-1">
              {selectedGoogleMapsLocation ? (
                <>
                  <span className="text-blue-600">
                    {selectedGoogleMapsLocation.formattedAddress}
                  </span>
                </>
              ) : (
                <>
                  {selectedNeighborhood?.name
                    ? `${selectedNeighborhood.name}, `
                    : ""}
                  {selectedLGA?.name ? `${selectedLGA.name}, ` : ""}
                  {selectedState?.name}, Nigeria
                </>
              )}
            </div>
          </div>
          {/* <div className="mt-1 text-xs text-text-light">
 {GOOGLE_MAPS_CONFIG.SECURITY_CONFIG.MESSAGES.PRIVACY_NOTICE}
 </div> */}
        </div>
      )}
    </div>
  );
}
